/**
 * Performance monitoring service for tracking Core Web Vitals and custom metrics
 */

export interface PerformanceMetric {
  name: string;
  value: number;
  rating: 'good' | 'needs-improvement' | 'poor';
  timestamp: number;
  id?: string;
}

export interface WebVitalsData {
  CLS: PerformanceMetric | null;
  FID: PerformanceMetric | null;
  FCP: PerformanceMetric | null;
  LCP: PerformanceMetric | null;
  TTFB: PerformanceMetric | null;
  INP: PerformanceMetric | null;
}

class PerformanceMonitor {
  private metrics: Map<string, PerformanceMetric> = new Map();
  private observers: Map<string, PerformanceObserver> = new Map();
  private webVitals: WebVitalsData = {
    CLS: null,
    FID: null,
    FCP: null,
    LCP: null,
    TTFB: null,
    INP: null,
  };

  constructor() {
    this.initWebVitalsMonitoring();
    this.initNavigationTiming();
    this.initResourceTiming();
    this.initLongTaskMonitoring();
  }

  /**
   * Initialize Core Web Vitals monitoring
   */
  private initWebVitalsMonitoring() {
    // Cumulative Layout Shift (CLS)
    if ('LayoutShiftAttribution' in window) {
      const observer = new PerformanceObserver((list) => {
        let cls = 0;
        for (const entry of list.getEntries()) {
          if (!entry.hadRecentInput) {
            cls += (entry as any).value;
          }
        }
        if (cls > 0) {
          this.recordMetric('CLS', cls);
          this.webVitals.CLS = this.getMetric('CLS');
        }
      });
      observer.observe({ type: 'layout-shift', buffered: true });
      this.observers.set('cls', observer);
    }

    // First Input Delay (FID)
    if ('PerformanceEventTiming' in window) {
      const observer = new PerformanceObserver((list) => {
        for (const entry of list.getEntries()) {
          const fid = entry.processingStart - entry.startTime;
          this.recordMetric('FID', fid);
          this.webVitals.FID = this.getMetric('FID');
        }
      });
      observer.observe({ type: 'first-input', buffered: true });
      this.observers.set('fid', observer);
    }

    // First Contentful Paint (FCP)
    const paintObserver = new PerformanceObserver((list) => {
      for (const entry of list.getEntries()) {
        if (entry.name === 'first-contentful-paint') {
          this.recordMetric('FCP', entry.startTime);
          this.webVitals.FCP = this.getMetric('FCP');
        }
      }
    });
    paintObserver.observe({ type: 'paint', buffered: true });
    this.observers.set('paint', paintObserver);

    // Largest Contentful Paint (LCP)
    const lcpObserver = new PerformanceObserver((list) => {
      const entries = list.getEntries();
      const lastEntry = entries[entries.length - 1];
      this.recordMetric('LCP', lastEntry.startTime);
      this.webVitals.LCP = this.getMetric('LCP');
    });
    lcpObserver.observe({ type: 'largest-contentful-paint', buffered: true });
    this.observers.set('lcp', lcpObserver);

    // Time to First Byte (TTFB)
    const navigationEntry = performance.getEntriesByType('navigation')[0] as PerformanceNavigationTiming;
    if (navigationEntry) {
      const ttfb = navigationEntry.responseStart - navigationEntry.requestStart;
      this.recordMetric('TTFB', ttfb);
      this.webVitals.TTFB = this.getMetric('TTFB');
    }
  }

  /**
   * Initialize navigation timing monitoring
   */
  private initNavigationTiming() {
    const observer = new PerformanceObserver((list) => {
      for (const entry of list.getEntries()) {
        const nav = entry as PerformanceNavigationTiming;
        
        // DNS lookup time
        this.recordMetric('DNS_LOOKUP', nav.domainLookupEnd - nav.domainLookupStart);
        
        // Connection time
        this.recordMetric('CONNECTION', nav.connectEnd - nav.connectStart);
        
        // Request/Response time
        this.recordMetric('REQUEST_RESPONSE', nav.responseEnd - nav.requestStart);
        
        // DOM processing time
        this.recordMetric('DOM_PROCESSING', nav.domComplete - nav.domLoading);
        
        // Page load time
        this.recordMetric('PAGE_LOAD', nav.loadEventEnd - nav.navigationStart);
      }
    });
    
    observer.observe({ type: 'navigation', buffered: true });
    this.observers.set('navigation', observer);
  }

  /**
   * Initialize resource timing monitoring
   */
  private initResourceTiming() {
    const observer = new PerformanceObserver((list) => {
      for (const entry of list.getEntries()) {
        const resource = entry as PerformanceResourceTiming;
        
        // Track slow resources (> 1s)
        if (resource.duration > 1000) {
          this.recordMetric(`SLOW_RESOURCE_${resource.name}`, resource.duration);
        }
        
        // Track resource sizes
        if (resource.transferSize) {
          this.recordMetric(`RESOURCE_SIZE_${resource.initiatorType}`, resource.transferSize);
        }
      }
    });
    
    observer.observe({ type: 'resource', buffered: true });
    this.observers.set('resource', observer);
  }

  /**
   * Initialize long task monitoring
   */
  private initLongTaskMonitoring() {
    if ('PerformanceLongTaskTiming' in window) {
      const observer = new PerformanceObserver((list) => {
        for (const entry of list.getEntries()) {
          this.recordMetric('LONG_TASK', entry.duration);
        }
      });
      observer.observe({ type: 'longtask', buffered: true });
      this.observers.set('longtask', observer);
    }
  }

  /**
   * Record a custom performance metric
   */
  recordMetric(name: string, value: number, id?: string): void {
    const rating = this.getRating(name, value);
    const metric: PerformanceMetric = {
      name,
      value,
      rating,
      timestamp: Date.now(),
      id,
    };
    
    this.metrics.set(`${name}_${Date.now()}`, metric);
    
    // Send to analytics if configured
    this.sendToAnalytics(metric);
  }

  /**
   * Get a specific metric by name (latest value)
   */
  getMetric(name: string): PerformanceMetric | null {
    const keys = Array.from(this.metrics.keys()).filter(key => key.startsWith(name));
    if (keys.length === 0) return null;
    
    const latestKey = keys.sort().pop();
    return latestKey ? this.metrics.get(latestKey) || null : null;
  }

  /**
   * Get all metrics
   */
  getAllMetrics(): PerformanceMetric[] {
    return Array.from(this.metrics.values());
  }

  /**
   * Get Web Vitals data
   */
  getWebVitals(): WebVitalsData {
    return this.webVitals;
  }

  /**
   * Determine performance rating based on metric thresholds
   */
  private getRating(name: string, value: number): 'good' | 'needs-improvement' | 'poor' {
    const thresholds: Record<string, { good: number; poor: number }> = {
      CLS: { good: 0.1, poor: 0.25 },
      FID: { good: 100, poor: 300 },
      FCP: { good: 1800, poor: 3000 },
      LCP: { good: 2500, poor: 4000 },
      TTFB: { good: 800, poor: 1800 },
      INP: { good: 200, poor: 500 },
      LONG_TASK: { good: 50, poor: 100 },
      DNS_LOOKUP: { good: 100, poor: 300 },
      CONNECTION: { good: 300, poor: 1000 },
    };

    const threshold = thresholds[name];
    if (!threshold) return 'good';

    if (value <= threshold.good) return 'good';
    if (value <= threshold.poor) return 'needs-improvement';
    return 'poor';
  }

  /**
   * Send metrics to analytics service
   */
  private sendToAnalytics(metric: PerformanceMetric): void {
    // Only send important metrics to avoid overwhelming the analytics service
    const importantMetrics = ['CLS', 'FID', 'FCP', 'LCP', 'TTFB', 'INP', 'LONG_TASK'];
    
    if (importantMetrics.includes(metric.name)) {
      // Send to your analytics service
      if (typeof window !== 'undefined' && 'fetch' in window) {
        fetch('/api/analytics/performance', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(metric),
        }).catch(() => {
          // Silently fail - don't impact user experience
        });
      }
    }
  }

  /**
   * Mark the start of a custom timing
   */
  markStart(name: string): void {
    performance.mark(`${name}_start`);
  }

  /**
   * Mark the end of a custom timing and record the duration
   */
  markEnd(name: string): void {
    performance.mark(`${name}_end`);
    performance.measure(name, `${name}_start`, `${name}_end`);
    
    const measure = performance.getEntriesByName(name, 'measure')[0];
    if (measure) {
      this.recordMetric(name, measure.duration);
    }
  }

  /**
   * Measure React component render time
   */
  measureComponentRender(componentName: string, callback: () => void): void {
    this.markStart(`component_${componentName}`);
    callback();
    this.markEnd(`component_${componentName}`);
  }

  /**
   * Monitor memory usage
   */
  getMemoryUsage(): any {
    if ('memory' in performance) {
      const memory = (performance as any).memory;
      return {
        usedJSHeapSize: memory.usedJSHeapSize,
        totalJSHeapSize: memory.totalJSHeapSize,
        jsHeapSizeLimit: memory.jsHeapSizeLimit,
      };
    }
    return null;
  }

  /**
   * Clean up observers
   */
  disconnect(): void {
    this.observers.forEach(observer => observer.disconnect());
    this.observers.clear();
  }
}

// Export singleton instance
export const performanceMonitor = new PerformanceMonitor();

// Helper functions for React components
export const withPerformanceMonitoring = <P extends object>(
  Component: React.ComponentType<P>,
  componentName: string
) => {
  return React.forwardRef<any, P>((props, ref) => {
    React.useEffect(() => {
      performanceMonitor.markStart(`component_${componentName}_mount`);
      return () => {
        performanceMonitor.markEnd(`component_${componentName}_mount`);
      };
    }, []);

    return React.createElement(Component, { ...props, ref });
  });
};

export { PerformanceMonitor };